// Pure Comparison population layer (04i-b), ported from js/renderers-comparison.js.
// The scatter plots the whole real player population (window.CatalystData.players — the full
// ~162-metric per-season stats Stats/Rankings/Filter share), NOT the narrow
// comparisonPopulation capture (which the prototype deliberately never reads here — it
// structurally can't support arbitrary-metric axes). Reuses the StatsFilterKit engine
// (matchesFilters) so the same compare-against filter system drives the plotted population;
// kept pure (population + categories passed in) so it's testable and unchanged at the Track-2
// cutover. The chart math lives in modules/comparison-view.ts (04i-a).

import type { ProtoMetricCategory, ProtoPlayerMetric } from "@catbento/shared";
import type { RawPlayerRecord } from "./snapshot.js";
import { seasonSortKey } from "./league.js";
import { matchesFilters, type ActiveFilters, type KitPlayer, type RangeFilterConfig } from "../engines/stats-filter-kit.js";

type Players = Record<string, RawPlayerRecord>;

/** Age in whole years from a dob string, or null. A local copy (the prototype duplicates this
 *  trivial helper per renderer file); used only by COMPARISON_RANGE_FILTERS below.
 *  renderers-comparison.js:39. */
function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/** ABSOLUTE population-wide ranges — a database-wide comparison has no single reference player
 *  to be relative to (same reasoning + same real domains as Rankings' RANKING_RANGE_FILTERS:
 *  15–42y, 151–185cm, 48–81kg). Passed as the trailing `rangeFilters` arg to matchesFilters and
 *  (04i-c) to <FilterChipBar>. renderers-comparison.js:132. */
export const COMPARISON_RANGE_FILTERS: RangeFilterConfig = {
  age: {
    label: "Age range",
    chipLabel: "Age",
    unit: "y",
    relative: false,
    domain: { min: 15, max: 42 },
    getValue: (p) => ageFromDob(p.dob ?? null),
  },
  height: {
    label: "Height range",
    chipLabel: "Height",
    unit: "cm",
    relative: false,
    domain: { min: 151, max: 185 },
    getValue: (p) => (typeof p.height === "number" ? p.height : null),
  },
  weight: {
    label: "Weight range",
    chipLabel: "Weight",
    unit: "kg",
    relative: false,
    domain: { min: 48, max: 81 },
    getValue: (p) => (typeof p.weight === "number" ? p.weight : null),
  },
};

/** Resolved catalog entry for one metric key (globally unique across the 5 categories, so a
 *  bare key is unambiguous — renderers-comparison.js:100). */
export interface ComparisonMetricEntry {
  metric: ProtoPlayerMetric;
  category: ProtoMetricCategory;
}

/** A plotted point: a real player with real x/y values in the selected season, plus optional
 *  size/colour metric values (null = plot at the fixed default size / colour). */
export interface ComparisonPoint {
  /** The player's key in the population: her name in the snapshot; live `"15620"`, or
   *  `"15620:full_back"` with a Position chip (one player can plot once per position). */
  key: string;
  player: RawPlayerRecord;
  x: number;
  y: number;
  size: number | null;
  color: number | null;
}

/** Every distinct season code with real recorded stats anywhere in the population, newest-first
 *  — a comparison spans everyone, not one player's seasons. Same computation as Rankings' /
 *  Filter's own season list (the prototype's per-file duplication of this trivial helper).
 *  renderers-comparison.js:66. */
export function comparisonSeasonCodes(players: Players): string[] {
  const codes = new Set<string>();
  for (const pl of Object.values(players)) {
    const seasons = pl.seasons ?? {};
    for (const code of Object.keys(seasons)) {
      if (Object.keys(seasons[code]?.stats ?? {}).length > 0) codes.add(code);
    }
  }
  return Array.from(codes).sort((a, b) => seasonSortKey(b) - seasonSortKey(a));
}

/** The 162 metrics grouped by CATEGORY for the axis pickers (X/Y/Size/Colour) — Comparison
 *  spans every category at once (an axis isn't locked to one the way a Ranking is), so 5 groups
 *  of up to ~40 is more scannable than the finer subcategory grouping. renderers-comparison.js:91.
 *  Return shape is structurally a StyledSelect group list — no import from the module layer. */
export function comparisonMetricGroups(
  categories: readonly ProtoMetricCategory[],
): { group: string; items: { value: string; label: string }[] }[] {
  return categories.map((cat) => ({
    group: cat.name,
    items: cat.subcategories.flatMap((sub) => sub.playerMetrics.map((m) => ({ value: m.key, label: m.name }))),
  }));
}

/** A flat key → {metric, category} lookup. Safe as a bare key here (unlike Rankings' composite
 *  categoryId+key) because Comparison never locks to one category, so there's no cross-category
 *  key ambiguity to guard against. renderers-comparison.js:108. */
export function buildComparisonMetricLookup(categories: readonly ProtoMetricCategory[]): Map<string, ComparisonMetricEntry> {
  const map = new Map<string, ComparisonMetricEntry>();
  for (const cat of categories) {
    for (const sub of cat.subcategories) {
      for (const m of sub.playerMetrics) map.set(m.key, { metric: m, category: cat });
    }
  }
  return map;
}

/** A player's real value for a metric in a season, or null when not a finite number.
 *  renderers-comparison.js:119. */
export function comparisonMetricValue(
  player: RawPlayerRecord,
  seasonCode: string,
  key: string,
  // Live (Ben, 2026-10-16, Q-016): Total = per 90 × 90s played, for a per-90 ("_90") metric only.
  mode: "per90" | "total" = "per90",
): number | null {
  const stats = player.seasons?.[seasonCode]?.stats ?? {};
  const v = stats[key];
  if (typeof v !== "number" || Number.isNaN(v)) return null;
  if (mode === "per90" || !key.endsWith("_90")) return v;
  const nineties = stats.nineties_played;
  if (typeof nineties !== "number" || Number.isNaN(nineties)) return null;
  return Math.round(v * nineties * 1e6) / 1e6;
}

export interface ComputeComparisonParams {
  players: Players;
  /** Active compare-against filters (default seed is { league: "FAWSL" }). */
  active: ActiveFilters;
  /** Purely-internal seed for a categorical chip's default value; never surfaced (every range
   *  filter here is absolute, so it isn't a reference point). renderers-comparison.js:677. */
  refPlayer: KitPlayer;
  seasonCode: string;
  xKey: string;
  yKey: string;
  /** Resolved metric key for bubble size, or null (fixed size). */
  sizeKey: string | null;
  /** Resolved metric key for colour, or null ("position" / "none" resolve to null — that's a
   *  render concern, not a plotted value). */
  colorKey: string | null;
  rangeFilters: RangeFilterConfig;
  /** Live only: Per 90 (default) or Total (Q-016). */
  mode?: "per90" | "total";
}

/** The plotted points: players passing the filters AND having a real value for BOTH required
 *  axes in the season (honestly dropped otherwise — no fabricated zero). Size/colour are
 *  embellishments, never a further exclusion: a player missing only those still plots at the
 *  fixed default. renderers-comparison.js:691. */
export function computeComparisonPoints(params: ComputeComparisonParams): ComparisonPoint[] {
  const { players, active, refPlayer, seasonCode, xKey, yKey, sizeKey, colorKey, rangeFilters, mode = "per90" } = params;
  const out: ComparisonPoint[] = [];
  for (const [key, pl] of Object.entries(players)) {
    if (!matchesFilters(pl as unknown as KitPlayer, active, refPlayer, rangeFilters)) continue;
    const x = comparisonMetricValue(pl, seasonCode, xKey, mode);
    const y = comparisonMetricValue(pl, seasonCode, yKey, mode);
    if (x == null || y == null) continue;
    out.push({
      key,
      player: pl,
      x,
      y,
      size: sizeKey ? comparisonMetricValue(pl, seasonCode, sizeKey, mode) : null,
      color: colorKey ? comparisonMetricValue(pl, seasonCode, colorKey, mode) : null,
    });
  }
  return out;
}
